import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import type { AxiosError } from 'axios'
import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useState } from 'react'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { errorToast, successToast } from 'v2/utils/toasts'
import { type BuddyFeedbackScore, submitBuddyFeedback } from '../buddy-feedback-service'
import type { BuddyFeedbackReason } from '../buddy-stream'
import type { BuddyFeedbackPrompt, BuddyUserContext } from '../use-buddy-chat'

export interface ShownFeedbackPrompt extends BuddyFeedbackPrompt {
  sessionId: string
  feedbackId: string
}

interface FeedbackDialogProps {
  open: boolean
  prompt: ShownFeedbackPrompt
  getUserContext: () => BuddyUserContext
  onClose: () => void
}

const COPY: Record<BuddyFeedbackReason, { title: string; description: string }> = {
  satisfied: { title: 'Glad that helped!', description: 'How was your chat with Buddy?' },
  done: { title: 'Thanks for chatting with Buddy', description: 'How did Buddy do?' },
  frustrated: {
    title: 'Sorry this was frustrating',
    description: 'How did Buddy do? Your feedback helps us improve.',
  },
}

const VOTES = [
  { score: 1, label: 'Helpful', Icon: ThumbsUp, testId: 'assistant-feedback-up-btn' },
  { score: 0, label: 'Not helpful', Icon: ThumbsDown, testId: 'assistant-feedback-down-btn' },
] as const

// Thumbs rating for a Buddy conversation, opened by the panel when the server asks for
// one. Owns the vote: a failure keeps the dialog open so the same vote can be retried.
export const FeedbackDialog = ({ open, prompt, getUserContext, onClose }: FeedbackDialogProps) => {
  const [pendingScore, setPendingScore] = useState<BuddyFeedbackScore | null>(null)
  const isSubmitting = pendingScore !== null

  const vote = async (score: BuddyFeedbackScore) => {
    setPendingScore(score)
    try {
      await submitBuddyFeedback({
        feedback_id: prompt.feedbackId,
        session_id: prompt.sessionId,
        feedback_type: 'thumbs',
        score,
        trigger: prompt.reason,
        api_id: prompt.apiId,
        source: 'console',
        userContext: getUserContext(),
      })
      successToast({ title: 'Thanks for the feedback!' })
      onClose()
    } catch (error) {
      // 400/409/429 are in the interceptor's ignoredStatusCodes, so nothing logs this but us.
      const status = (error as AxiosError | undefined)?.response?.status
      track(AnalyticsEvents.BUDDY_FEEDBACK_FAILED, {
        status_code: status ?? null,
        reason: prompt.reason,
        session_id: prompt.sessionId,
      })
      errorToast({ title: "Couldn't send feedback", description: 'Please try again.' })
    } finally {
      setPendingScore(null)
    }
  }

  // X, Esc, the overlay and "Not now" all land here; a vote in flight must finish first.
  const dismiss = () => {
    if (isSubmitting) return
    track(AnalyticsEvents.BUDDY_FEEDBACK_DISMISSED, {
      reason: prompt.reason,
      session_id: prompt.sessionId,
    })
    onClose()
  }

  const { title, description } = COPY[prompt.reason]

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss()
      }}
    >
      <DialogContent className="max-w-[400px]" data-testid="assistant-feedback-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {VOTES.map(({ score, label, Icon, testId }) => (
            <Button
              key={score}
              variant="outline"
              className="h-auto flex-col gap-2 py-4"
              disabled={isSubmitting}
              aria-busy={pendingScore === score}
              onClick={() => vote(score)}
              data-testid={testId}
            >
              {pendingScore === score ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Icon className="w-5 h-5" />
              )}
              <span className="text-p-500">{label}</span>
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            disabled={isSubmitting}
            data-testid="assistant-feedback-skip-btn"
          >
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
